import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import PsychologyAltRoundedIcon from "@mui/icons-material/PsychologyAltRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import Header from "../header_h/Header";
import Footer from "../footer/Footer";
import BreadcrumbsModulePage from "../breadcrumbs/BreadcrumbsModulePage";
import DownloadPdfButton from "../download_pdf_button/DownloadPdfButton";
import { modulesData } from "./modulesData";
import { getModuleSections, getModulesByAuthor } from "../../api/subjects";
import "../class_page/class_page.css";
import "../selection_container/SelectionContainer.css";

const normalize = (v) => String(v ?? "").trim();
const lower = (v) => normalize(v).toLowerCase();

const getLegacyModulesSafe = (data, id, subject, author) => {
  if (!data) return [];

  const idStr = normalize(id);
  const idNumStr = String(Number(idStr));
  const subj = normalize(subject);
  const auth = normalize(author);

  const direct = data?.[idStr]?.[subj]?.[auth] || data?.[idNumStr]?.[subj]?.[auth] || [];

  if (Array.isArray(direct) && direct.length) return direct;

  const classKeys = Object.keys(data || {});
  const classKey =
    classKeys.find((k) => normalize(k) === idStr) ||
    classKeys.find((k) => normalize(k) === idNumStr) ||
    classKeys.find((k) => lower(k) === lower(idStr)) ||
    classKeys.find((k) => lower(k) === lower(idNumStr));

  if (!classKey) return [];

  const subjObj = data[classKey] || {};
  const subjKeys = Object.keys(subjObj || {});
  const subjKey =
    subjKeys.find((k) => normalize(k) === subj) ||
    subjKeys.find((k) => lower(k) === lower(subj));

  if (!subjKey) return [];

  const authObj = subjObj[subjKey] || {};
  const authKeys = Object.keys(authObj || {});
  const authKey =
    authKeys.find((k) => normalize(k) === auth) ||
    authKeys.find((k) => lower(k) === lower(auth));

  if (!authKey) return [];

  const arr = authObj[authKey];
  return Array.isArray(arr) ? arr : [];
};

const normalizeSlug = (module) => {
  const slug = module?.moduleSlug ?? module?.path ?? "";
  return normalize(slug);
};

const slugifySection = (value) =>
  lower(value)
    .replace(/[^a-z0-9а-яіїєґ]+/gi, "-")
    .replace(/^-+|-+$/g, "");

const normalizeSection = (section) => {
  const title = normalize(section?.title);
  const sectionSlug = normalize(section?.sectionSlug || section?.slug) || slugifySection(title);

  return {
    ...section,
    sectionSlug,
    title,
    description: normalize(section?.description),
    iconKey: normalize(section?.iconKey) || "test",
    order: Number(section?.order ?? 0) || 0,
    enabled: section?.enabled !== false,
  };
};

const sectionIcon = (iconKey, idx) => {
  if (iconKey === "test") return <PsychologyAltRoundedIcon />;
  if (iconKey === "interactive") return <TouchAppRoundedIcon />;
  if (iconKey === "video") return <OndemandVideoRoundedIcon />;

  const fallbackIcons = [
    <PsychologyAltRoundedIcon />,
    <TouchAppRoundedIcon />,
    <OndemandVideoRoundedIcon />,
  ];
  return fallbackIcons[idx % fallbackIcons.length];
};

const mergeModules = (legacy, mongo) => {
  const map = new Map();

  (Array.isArray(legacy) ? legacy : []).forEach((module, idx) => {
    const slug = normalizeSlug(module);
    if (!slug) return;
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(module?.title),
      order: typeof module?.order === "number" ? module.order : idx,
      enabled: true,
      pdfPath: module?.pdfPath || null,
      pdfFileName: module?.pdfFileName || null,
      sectionSlug: normalize(module?.sectionSlug),
      sectionTitle: normalize(module?.sectionTitle),
      sectionDescription: normalize(module?.sectionDescription),
      sectionIconKey: normalize(module?.sectionIconKey),
      sectionOrder: typeof module?.sectionOrder === "number" ? module.sectionOrder : 0,
      __source: "legacy",
    });
  });

  (Array.isArray(mongo) ? mongo : []).forEach((module, idx) => {
    const slug = normalizeSlug(module);
    if (!slug) return;

    const prev = map.get(slug);
    map.set(slug, {
      moduleSlug: slug,
      title: normalize(module?.title ?? prev?.title),
      order:
        typeof module?.order === "number"
          ? module.order
          : typeof prev?.order === "number"
            ? prev.order
            : idx,
      enabled:
        typeof module?.enabled === "boolean"
          ? module.enabled
          : typeof prev?.enabled === "boolean"
            ? prev.enabled
            : true,
      pdfPath: module?.pdfPath ?? prev?.pdfPath ?? null,
      pdfFileName: module?.pdfFileName ?? prev?.pdfFileName ?? null,
      sectionSlug: normalize(module?.sectionSlug ?? prev?.sectionSlug),
      sectionTitle: normalize(module?.sectionTitle ?? prev?.sectionTitle),
      sectionDescription: normalize(module?.sectionDescription ?? prev?.sectionDescription),
      sectionIconKey: normalize(module?.sectionIconKey ?? prev?.sectionIconKey),
      sectionOrder:
        typeof module?.sectionOrder === "number"
          ? module.sectionOrder
          : typeof prev?.sectionOrder === "number"
            ? prev.sectionOrder
            : 0,
      __source: prev ? "merged" : "mongo",
    });
  });

  return Array.from(map.values())
    .filter((module) => module.enabled !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const AuthorPage = ({ userName, userRoles }) => {
  const { id, subject, author } = useParams();
  const navigate = useNavigate();

  const [mongoModules, setMongoModules] = useState([]);
  const [authorSections, setAuthorSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState("");

  const legacyModules = useMemo(() => {
    return getLegacyModulesSafe(modulesData, id, subject, author);
  }, [id, subject, author]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!id || !subject || !author) return;
      setLoading(true);
      try {
        const data = await getModulesByAuthor(id, subject, author);
        if (mounted) setMongoModules(Array.isArray(data) ? data : []);
        try {
          const sectionsData = await getModuleSections(id, subject, author);
          if (mounted) {
            setAuthorSections(
              Array.isArray(sectionsData)
                ? sectionsData.map(normalizeSection).filter((section) => section.sectionSlug)
                : []
            );
          }
        } catch (sectionError) {
          if (mounted) setAuthorSections([]);
        }
      } catch (e) {
        if (mounted) setMongoModules([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [id, subject, author]);

  const modules = useMemo(() => {
    return mergeModules(legacyModules, mongoModules);
  }, [legacyModules, mongoModules]);

  useEffect(() => {
    setSelectedSection("");
  }, [id, subject, author]);

  const sections = useMemo(() => {
    const map = new Map();

    authorSections.forEach((section) => {
      if (!section.sectionSlug || !section.title) return;
      map.set(section.sectionSlug, {
        ...section,
        slug: section.sectionSlug,
        modules: [],
      });
    });

    modules.forEach((module) => {
      const title = normalize(module.sectionTitle);
      const slug = normalize(module.sectionSlug) || slugifySection(title);
      if (!slug || !title) return;

      const prev = map.get(slug);
      map.set(slug, {
        slug,
        title: prev?.title || title,
        description: prev?.description || normalize(module.sectionDescription),
        iconKey: prev?.iconKey || normalize(module.sectionIconKey) || "test",
        order:
          typeof prev?.order === "number"
            ? prev.order
            : typeof module.sectionOrder === "number"
            ? module.sectionOrder
            : 0,
        modules: [...(prev?.modules || []), module],
      });
    });

    const unsectioned = modules.filter((module) => {
      const title = normalize(module.sectionTitle);
      const slug = normalize(module.sectionSlug) || slugifySection(title);
      return !slug || !title;
    });

    const hasSectionedModules = Array.from(map.values()).some((section) => section.modules.length > 0);

    if (hasSectionedModules && unsectioned.length > 0) {
      map.set("__other", {
        slug: "__other",
        title: "Інші модулі",
        description: "Модулі без окремої секції",
        order: 9999,
        modules: unsectioned,
      });
    }

    return Array.from(map.values())
      .filter((section) => section.modules.length > 0)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [authorSections, modules]);

  const visibleModules = useMemo(() => {
    if (!sections.length || !selectedSection) return modules;
    return sections.find((section) => section.slug === selectedSection)?.modules || [];
  }, [modules, sections, selectedSection]);

  const selectedSectionMeta = useMemo(() => {
    if (!selectedSection) return null;
    return sections.find((section) => section.slug === selectedSection) || null;
  }, [sections, selectedSection]);

  const showSectionPicker = sections.some((section) => section.slug !== "__other") && !selectedSection;

  return (
    <>
      <Header userName={userName} userRoles={userRoles} />
      <BreadcrumbsModulePage />

      <Container maxWidth="md" className="catalog-page">
        <Box className="catalog-page__heading">
          <Typography variant="h4" component="h1">
            Модулі
          </Typography>
          <Typography color="text.secondary">
            Оберіть модуль для проходження або завантажте PDF, якщо він доступний.
          </Typography>
        </Box>

        {showSectionPicker ? (
          <Paper className="module-type-panel" variant="outlined">
            <Typography variant="h5" component="h2">
              Оберіть тип завдань
            </Typography>
            <List disablePadding className="module-type-list">
              {sections.map((section, idx) => (
                <ListItemButton
                  key={section.slug}
                  className="module-type-list__item"
                  onClick={() => setSelectedSection(section.slug)}
                >
                  <ListItemIcon>{sectionIcon(section.iconKey, idx)}</ListItemIcon>
                  <ListItemText primary={section.title} secondary={section.description} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        ) : (
          <Paper className="catalog-panel" variant="outlined">
            {selectedSection && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Обраний тип
                  </Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {selectedSectionMeta?.title || "Модулі"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => setSelectedSection("")}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  До типів завдань
                </Button>
              </Box>
            )}

            {visibleModules.length > 0 ? (
              <List disablePadding className="catalog-list">
                {visibleModules.map((module) => (
                  <Box key={module.moduleSlug} className="module-list-row">
                    <ListItemButton
                      className="catalog-list__item"
                      onClick={() =>
                        navigate(`/class/${id}/${subject}/${author}/modules/${module.moduleSlug}`)
                      }
                    >
                      <ListItemText primary={module.title} />
                      <ChevronRightRoundedIcon />
                    </ListItemButton>

                    {module.pdfPath && (
                      <DownloadPdfButton
                        pdfPath={module.pdfPath}
                        pdfFileName={module.pdfFileName}
                      />
                    )}
                  </Box>
                ))}
              </List>
            ) : (
              <Box className="catalog-state">
                {loading && <CircularProgress size={28} />}
                <Typography>
                  {loading ? "Завантаження..." : "Модулі не знайдені"}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Container>
      <Footer />
    </>
  );
};

export default AuthorPage;
